import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { formatDuration, minutesToHm, type AvailabilitySlot, type MinuteRange } from '@reditelji/shared';
import type { PoolVolunteer } from '../api/types';
import { slotLabel, trimFor } from './derive';

/**
 * What the coordinator is about to do, and everything about it worth knowing
 * before it happens.
 *
 * One dialog carries every fact, rather than a chain of them: a closed signup
 * slot, a schedule this person already turned down, and a briefing the change
 * would retire are three reasons to hesitate over the *same* assignment, and
 * answering them one at a time would ask three times for one decision.
 */
export interface PendingAssign {
  volunteer: PoolVolunteer;
  postId: string;
  postCode: string;
  postMicro: string;
  range: MinuteRange;
  dayKey: string;
  /** Slots this range runs into that the volunteer marked "ni na voljo". */
  closed: AvailabilitySlot[];
  /** Whether this overrules something they said (`declinedOnDay`): a shift they turned down on this day. */
  declined: boolean;
  /** Those shifts, named ("ne more: GM3 · Pet 11:45–16:30"). */
  refusals: string[];
  /**
   * A row of theirs the range runs into that day ("KG2 08:00–12:00"): one
   * person cannot stand at two posts, but a coordinator may be moving them.
   */
  clash?: string;
  /** Their day would pass the daily maximum: what it would be, and the maximum. */
  overMax?: { afterMin: number; maxMin: number };
  /**
   * Whether a shorter range would still stand them at another post or past the
   * maximum. A trim only cuts out closed hours, so it is offered only when it
   * settles these too — the blue button must never be the one that double-books.
   */
  stillClashes?: (range: MinuteRange) => boolean;
  /** Accepting retires their briefing and asks them again — silent edits do not. */
  retiresBriefing: boolean;
  /**
   * The person currently holding this shift, when the assignment displaces
   * somebody who has been briefed: their schedule is reset too, and the dialog
   * has to say so — it is the one fact the coordinator cannot see on the board.
   */
  displaced?: string;
  /**
   * Whoever holds the shift this assignment takes over, briefed or not. A drop
   * onto somebody's block would otherwise take their place without a word.
   */
  replaces?: string;
  /** Run once the coordinator says yes, over whichever range they chose. */
  commit: (range: MinuteRange) => void;
}

/**
 * Whether an assignment asks first: it bends what the person said about their
 * day, stands them at two posts at once, runs their day past the maximum, or
 * takes the shift from somebody else. Only a clean assignment to an empty slot
 * goes straight through — the same line the drag preview draws in red.
 */
export function mustAsk(p: {
  closed: readonly unknown[];
  declined: boolean;
  replaces?: string;
  clash?: string;
  overMax?: unknown;
}): boolean {
  return (
    p.closed.length > 0 ||
    p.declined ||
    p.replaces !== undefined ||
    p.clash !== undefined ||
    p.overMax !== undefined
  );
}

/**
 * The part of the range this person did not close, longest first — or null
 * when there is nothing to trim: every touched slot closed, or none of them.
 *
 * Offered as an action because trimming is usually the better answer than
 * overruling: the coordinator keeps the hours the volunteer actually offered
 * and the rest goes back to being a gap somebody else can take. Works in either
 * direction — a closed morning moves the start, a closed evening moves the end.
 * A piece that is the whole range is not a trim, so it is never offered: a
 * button that "trims" to the full range would read as the safe choice while
 * overruling a declined schedule exactly as the orange one does.
 */
export function trimTarget(p: PendingAssign): MinuteRange | null {
  const trim = trimFor(p.volunteer.availability, p.dayKey, p.range);
  return trim && p.stillClashes?.(trim) ? null : trim;
}

const rangeLabel = (r: MinuteRange) => `${minutesToHm(r.fromMin)}–${minutesToHm(r.toMin)}`;

function Fact({ icon, tone, children }: { icon: string; tone: string; children: React.ReactNode }) {
  return (
    <Group gap={7} wrap="nowrap" align="flex-start">
      <span style={{ color: tone, fontWeight: 700, width: '1.05rem', textAlign: 'center', flexShrink: 0 }}>
        {icon}
      </span>
      <Text size="sm" style={{ minWidth: 0 }}>
        {children}
      </Text>
    </Group>
  );
}

export function AvailabilityConfirm({
  pending,
  onCancel,
  onAssign,
}: {
  pending: PendingAssign | null;
  onCancel: () => void;
  /** Commit the assignment over `range` — the full one, or the trimmed one. */
  onAssign: (range: MinuteRange) => void;
}) {
  const trim = pending ? trimTarget(pending) : null;
  // The hours they did offer, even when those still clash and so are not offered as a trim.
  const offered = pending ? trimFor(pending.volunteer.availability, pending.dayKey, pending.range) : null;
  const declined = pending?.declined ?? false;
  // Nothing about the newcomer is in question — only whose shift it is.
  const onlyReplaces =
    pending !== null &&
    !declined &&
    pending.closed.length === 0 &&
    pending.clash === undefined &&
    pending.overMax === undefined;
  return (
    <Modal
      opened={pending !== null}
      onClose={onCancel}
      size="md"
      // Never a gendered participle: Slovenian would need a spol the timeline
      // payload does not carry, and a third of the roster has none recorded.
      title={
        pending
          ? `${pending.volunteer.name} — ${
              declined
                ? 'zavrnjena izmena'
                : pending.closed.length > 0
                  ? 'ni na voljo v tem terminu'
                  : pending.clash
                    ? `takrat že na ${pending.clash}`
                    : pending.overMax
                      ? 'čez dnevno mejo'
                      : `namesto ${pending.replaces ?? ''}`
            }`
          : ''
      }
    >
      {pending ? (
        <Stack gap={10}>
          <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--rr-font-mono)' }}>
            {pending.postCode} · {pending.postMicro} · {rangeLabel(pending.range)}
          </Text>

          {declined
            ? pending.refusals.map((r) => (
                <Fact key={r} icon="✖" tone="var(--rr-danger)">
                  {r.charAt(0).toUpperCase() + r.slice(1)}.
                </Fact>
              ))
            : null}

          {pending.closed.length > 0 ? (
            <Fact icon="▲" tone="var(--rr-warn)">
              <b>{pending.closed.map(slotLabel).join(', ')}</b> — v prijavnici označeno »ni na
              voljo«.
            </Fact>
          ) : null}

          {pending.clash ? (
            <Fact icon="⇆" tone="var(--rr-danger)">
              Takrat je že na <b>{pending.clash}</b> — dve mesti hkrati.
            </Fact>
          ) : null}

          {pending.overMax ? (
            <Fact icon="▲" tone="var(--rr-warn)">
              Dan bi imel <b>{formatDuration(pending.overMax.afterMin)}</b>, dnevna meja je{' '}
              {formatDuration(pending.overMax.maxMin)}.
            </Fact>
          ) : null}

          {pending.retiresBriefing ? (
            <Fact icon="✉" tone="var(--rr-warn)">
              Brifing je že poslan — razpored gre v <b>ponovno potrditev</b>.
            </Fact>
          ) : null}

          {pending.displaced ? (
            <Fact icon="✉" tone="var(--rr-warn)">
              Zamenja <b>{pending.displaced}</b>, ki brifing že ima — tudi ta razpored gre v{' '}
              <b>ponovno potrditev</b>.
            </Fact>
          ) : pending.replaces ? (
            <Fact icon="⇄" tone="var(--rr-warn)">
              Zamenja <b>{pending.replaces}</b> na tej izmeni.
            </Fact>
          ) : null}

          {pending.closed.length > 0 || declined ? (
            <Text
              size="xs"
              c="dimmed"
              style={{ background: 'var(--rr-surface-muted)', borderRadius: 6, padding: '6px 9px' }}
            >
              {pending.closed.length === 0
                ? 'Razpoložljivost ni sporna — sporna je zavrnitev.'
                : trim
                  ? `Na voljo ${rangeLabel(trim)}.`
                  : offered
                    ? `Na voljo ${rangeLabel(offered)}, a tudi tam ostane zgoraj navedeno.`
                    : 'Celotna izmena leži v zaprtem terminu.'}
            </Text>
          ) : null}

          <Group justify="flex-end" gap={6}>
            {trim ? (
              <Button size="compact-sm" onClick={() => onAssign(trim)}>
                Dodeli {rangeLabel(trim)}
              </Button>
            ) : null}
            {onlyReplaces ? (
              <Button size="compact-sm" onClick={() => onAssign(pending.range)}>
                Zamenjaj
              </Button>
            ) : (
              <Button
                size="compact-sm"
                variant="light"
                color="orange"
                onClick={() => onAssign(pending.range)}
              >
                {trim ? 'Vseeno dodeli celotno' : 'Vseeno dodeli'}
              </Button>
            )}
            <Button size="compact-sm" variant="default" onClick={onCancel}>
              Prekliči
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}
